import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { format } from 'date-fns';
import { formatCurrency } from '../utils/format';

const COLS = [
  { key: 'tenant_name',           label: 'Cliente',                  align: 'left'  },
  { key: 'auction_count',         label: 'Remates',                  align: 'right' },
  { key: 'total_lots',            label: 'Lotes',                    align: 'right' },
  { key: 'avg_participants',      label: 'Prom. Part. / Remate',     align: 'right' },
  { key: 'total_participants',    label: 'Total Participantes',      align: 'right' },
  { key: 'total_sold',            label: 'Total Vendido',            align: 'right' },
  { key: 'total_commission',      label: 'Total Comisión',           align: 'right' },
  { key: 'commission_pct',        label: '% Comisión',               align: 'right' },
  { key: 'avg_unadjudicated_pct', label: '% Prom. No Adjudicado',   align: 'right' },
];

/**
 * A small visual bar to represent a percentage value.
 */
function PctBar({ value, color }) {
  const pct = Math.min(Math.max(parseFloat(value) || 0, 0), 100);
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'flex-end' }}>
      <div style={{
        width: 60,
        height: 5,
        background: '#E2E5EC',
        borderRadius: 9999,
        overflow: 'hidden',
      }}>
        <div style={{
          width: `${pct}%`,
          height: '100%',
          background: color,
          borderRadius: 9999,
          transition: 'width 0.4s ease',
        }} />
      </div>
      <span style={{ minWidth: 40, textAlign: 'right' }}>{pct.toFixed(1)}%</span>
    </div>
  );
}

PctBar.propTypes = {
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  color: PropTypes.string.isRequired,
};

// Only in the yearly table: when the client was created, flagged if created that year.
const CREATED_COL = { key: 'tenant_created_at', label: 'Creado', align: 'left' };

/**
 * Per-tenant statistics table with sortable columns.
 * @param {'month'|'year'} period - Labels the lots column and, for 'year', adds the "Creado" column.
 */
function TenantStatsTable({ stats, period = 'month' }) {
  const isYear = period === 'year';
  const cols = isYear
    ? [COLS[0], CREATED_COL, ...COLS.slice(1)]
    : COLS;
  const lotsLabel = isYear ? 'Lotes (año)' : 'Lotes (mes)';
  const [sortKey, setSortKey] = useState('tenant_name');
  const [sortDir, setSortDir] = useState('asc');

  const handleSort = (key) => {
    if (sortKey === key) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  const sorted = [...stats].sort((a, b) => {
    const av = a[sortKey];
    const bv = b[sortKey];
    const numA = parseFloat(av);
    const numB = parseFloat(bv);
    // ISO dates would parse as just the year, so compare them as strings.
    const isNum = sortKey !== 'tenant_created_at' && !isNaN(numA) && !isNaN(numB);
    let cmp = isNum ? numA - numB : String(av).localeCompare(String(bv));
    return sortDir === 'asc' ? cmp : -cmp;
  });

  if (!stats.length) {
    return (
      <div style={{ textAlign: 'center', padding: '40px 0', color: '#9AA3B5', fontSize: '0.9rem' }}>
        Sin datos para este período.
      </div>
    );
  }

  const thStyle = (key) => ({
    padding: '10px 14px',
    fontSize: '0.6875rem',
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
    color: sortKey === key ? '#1F50FF' : '#6B7A99',
    cursor: 'pointer',
    userSelect: 'none',
    whiteSpace: 'nowrap',
    borderBottom: '2px solid #E2E5EC',
    background: '#F8F9FC',
    transition: 'color 0.15s',
  });

  const tdStyle = (align) => ({
    padding: '11px 14px',
    fontSize: '0.875rem',
    color: '#021344',
    borderBottom: '1px solid #F0F2F8',
    textAlign: align,
    verticalAlign: 'middle',
  });

  const SortIcon = ({ colKey }) => {
    if (sortKey !== colKey) return <span style={{ color: '#CBD0DB', marginLeft: 4 }}>↕</span>;
    return <span style={{ color: '#1F50FF', marginLeft: 4 }}>{sortDir === 'asc' ? '↑' : '↓'}</span>;
  };

  return (
    <div style={{ overflowX: 'auto', borderRadius: 10, border: '1px solid #E2E5EC', boxShadow: '0 1px 3px rgba(2,19,68,0.06)' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 1000 }}>
        <thead>
          <tr>
            {cols.map(col => (
              <th
                key={col.key}
                style={{ ...thStyle(col.key), textAlign: col.align }}
                onClick={() => handleSort(col.key)}
              >
                {col.key === 'total_lots' ? lotsLabel : col.label}
                <SortIcon colKey={col.key} />
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {sorted.map((row, i) => (
            <tr key={row.tenant_id} style={{ background: i % 2 === 0 ? '#fff' : '#F8F9FC' }}>
              <td style={tdStyle('left')}>
                <span style={{ fontWeight: 500, color: '#021344' }}>{row.tenant_name}</span>
              </td>
              {isYear && (
                <td style={{ ...tdStyle('left'), whiteSpace: 'nowrap' }}>
                  {row.tenant_created_at ? format(new Date(row.tenant_created_at), 'dd/MM/yyyy') : '—'}
                  {row.created_in_period && (
                    <span style={{
                      marginLeft: 8,
                      fontSize: '0.6875rem',
                      fontWeight: 600,
                      padding: '2px 8px',
                      borderRadius: 9999,
                      background: '#F3E8FF',
                      color: '#7C3AED',
                    }}>
                      Nuevo
                    </span>
                  )}
                </td>
              )}
              <td style={tdStyle('right')}>
                <span style={{
                  display: 'inline-block',
                  minWidth: 28,
                  textAlign: 'center',
                  fontWeight: 600,
                  background: '#EEF2FF',
                  color: '#1F50FF',
                  borderRadius: 9999,
                  padding: '2px 10px',
                  fontSize: '0.8125rem',
                }}>
                  {row.auction_count}
                </span>
              </td>
              <td style={tdStyle('right')}>{row.total_lots}</td>
              <td style={tdStyle('right')}>{row.avg_participants}</td>
              <td style={tdStyle('right')}>
                <span style={{ fontWeight: 500 }}>{row.total_participants}</span>
              </td>
              <td style={tdStyle('right')}>{formatCurrency(row.total_sold)}</td>
              <td style={tdStyle('right')}>{formatCurrency(row.total_commission)}</td>
              <td style={tdStyle('right')}>{row.commission_pct ?? '—'}%</td>
              <td style={tdStyle('right')}>
                <PctBar
                  value={row.avg_unadjudicated_pct}
                  color={
                    parseFloat(row.avg_unadjudicated_pct) > 50
                      ? '#DC2626'
                      : parseFloat(row.avg_unadjudicated_pct) > 25
                        ? '#D97706'
                        : '#16A34A'
                  }
                />
              </td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr style={{ background: '#F0F2F8' }}>
            <td style={{ ...tdStyle('left'), fontWeight: 600, color: '#6B7A99', fontSize: '0.75rem', letterSpacing: '0.04em' }}>
              TOTALES
            </td>
            {isYear && (
              <td style={{ ...tdStyle('left'), fontWeight: 600, whiteSpace: 'nowrap' }}>
                {sorted.filter(r => r.created_in_period).length} nuevos
              </td>
            )}
            <td style={{ ...tdStyle('right'), fontWeight: 600 }}>
              {sorted.reduce((s, r) => s + parseInt(r.auction_count || 0, 10), 0)}
            </td>
            <td style={{ ...tdStyle('right'), fontWeight: 600 }}>
              {sorted.reduce((s, r) => s + parseInt(r.total_lots || 0, 10), 0)}
            </td>
            <td style={{ ...tdStyle('right'), fontWeight: 600 }}>
              {sorted.length
                ? (sorted.reduce((s, r) => s + parseFloat(r.avg_participants || 0), 0) / sorted.length).toFixed(2)
                : '—'}
            </td>
            <td style={{ ...tdStyle('right'), fontWeight: 600 }}>
              {sorted.reduce((s, r) => s + parseInt(r.total_participants || 0, 10), 0)}
            </td>
            <td style={{ ...tdStyle('right'), fontWeight: 600 }}>
              {formatCurrency(sorted.reduce((s, r) => s + parseFloat(r.total_sold || 0), 0))}
            </td>
            <td style={{ ...tdStyle('right'), fontWeight: 600 }}>
              {formatCurrency(sorted.reduce((s, r) => s + parseFloat(r.total_commission || 0), 0))}
            </td>
            <td style={{ ...tdStyle('right'), fontWeight: 600 }}>
              {(() => {
                const totalSold = sorted.reduce((s, r) => s + parseFloat(r.total_sold || 0), 0);
                const totalCommission = sorted.reduce((s, r) => s + parseFloat(r.total_commission || 0), 0);
                return totalSold > 0 ? `${((totalCommission / totalSold) * 100).toFixed(2)}%` : '—';
              })()}
            </td>
            <td style={{ ...tdStyle('right'), fontWeight: 600 }}>
              <PctBar
                value={
                  sorted.length
                    ? sorted.reduce((s, r) => s + parseFloat(r.avg_unadjudicated_pct || 0), 0) / sorted.length
                    : 0
                }
                color="#1F50FF"
              />
            </td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
}

TenantStatsTable.propTypes = {
  stats: PropTypes.arrayOf(PropTypes.shape({
    tenant_id:             PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    tenant_name:           PropTypes.string,
    auction_count:         PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    total_lots:            PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    avg_participants:      PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    total_participants:    PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    total_sold:            PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    total_commission:      PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    commission_pct:        PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    avg_unadjudicated_pct: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    tenant_created_at:     PropTypes.string,
    created_in_period:     PropTypes.bool,
  })).isRequired,
  period: PropTypes.oneOf(['month', 'year']),
};

export default TenantStatsTable;
